import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { GUEST_ACTION_NOT_AVAILABLE_MSG, GUEST_ORDERING_DISABLED_MSG, isGuestUser } from '../models/guest-user';
import { AuthenticationService } from './authentication.service';
import { SanitizeUtil, UNSERIALIZABLE_REQUEST_BODY } from './sanitize.util';
import { UtilityService } from './utility.service';

type GuestRefusalOutcome = 'refused' | 'guestOrderingDisabled' | 'sessionEnded';

export class Interceptor implements HttpInterceptor {

	// XD-62456: the one check in flight for a guest's refused calls. Several refused calls at once share it instead
	// of each POSTing IsAlive; it is dropped when it settles so the next refusal checks again.
	private guestSessionCheck$: Observable<GuestRefusalOutcome> | null = null;

	constructor(private _authenticationService: AuthenticationService,
				private _utilityService: UtilityService) {
	}

	intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		if (req.url.includes('CXTWCF')) {
			const user = JSON.parse(localStorage.getItem('Rapidship:user'));
			if (user && user.token) {
				req = req.clone({setHeaders: {Authorization: `Token ${user.token}`}});
			}
			if (!req.headers.has('Content-Type')) {
				req = req.clone({setHeaders: {'Content-Type': 'application/json'}});
			}
			req = req.clone({setHeaders: {Accept: 'application/json'}});
			req = req.clone({setHeaders: {CallingApplication: 'Rapidship'}});

			// While a login MFA challenge or enrollment is pending, the session token is PreAuth-scoped and
			// only the MFA endpoints are authorized (mirrors the backend PreAuthAllowList). Any full-auth
			// call attempted in this window would 401 by design, so short-circuit it here — the request
			// never leaves the browser. This keeps the console clean and, more importantly, keeps the
			// backend log sinks (Sentry) free of expected-failure 401 noise. The 401 handler below remains
			// as a defence-in-depth safety net for anything that slips through. Note: mfaNotifyPending is
			// intentionally excluded — the "notify" path does not downgrade the token, so it stays Active.
			const preAuthTokenActive = !!(user && (user.mfaChallengePending || user.mfaEnrollmentPending));
			// F-163: anchor the segment end so this security allowlist cannot match a longer sibling route
			// (e.g. /v2/AuthenticationLog, /v2/MfaEnrollmentAudit) by substring.
			const isPreAuthAllowed = /\/v2\/(MfaChallenge|MfaEnrollment|Authentication)(\/|$)/i.test(req.url);
			if (preAuthTokenActive && !isPreAuthAllowed) {
				return of(null);
			}
		}

		return next.handle(req).pipe(tap((event: HttpEvent<any>) => {
				if (event instanceof HttpResponse) {
					if (event.status === 200 && event.url.includes(localStorage.getItem('Rapidship:apiUrl'))) {
						const twentyMinutesLater = new Date();
						twentyMinutesLater.setMinutes(twentyMinutesLater.getMinutes() + 20);
						this._authenticationService.expirationOfSession = twentyMinutesLater; // set 20 minute sessionTimeout
					}
				}
			}), catchError((error: any) => {
				// Request body on the error, for the log: base64 stripped, then every credential and card field dropped.
				if (req.body) {
					try {
						error.body = SanitizeUtil.redactRequestBody(this._utilityService.copyAndRemoveBase64Data(req.body));
					} catch {
						error.body = UNSERIALIZABLE_REQUEST_BODY;
					}
				}
				// Need to logout the user if error message includes CXT Software requires a valid license
				if (error.status === 401 || error?.error?.detailedMsg?.includes('CXT Software requires a valid license') && !error?.url?.includes('AnonymousOptions')) {
					// While a login MFA challenge or enrollment is pending, the session token is intentionally
					// PreAuth-scoped: only the MFA endpoints are authorized, so any background or non-MFA
					// call 401s by design. Surfacing the global "logged out" modal for that expected 401
					// tears down the challenge screen the user is actively completing. In this window the
					// only session-enders are MFA completion (handled in the dialog, which upgrades the
					// token) or an explicit cancel (which calls authenticationDelete directly). mfaNotifyPending
					// is excluded — that path keeps the token Active, so a 401 there is a genuine logout.
					const currentUser = JSON.parse(localStorage.getItem('Rapidship:user'));
					const preAuthTokenActive = !!(currentUser && (currentUser.mfaChallengePending
						|| currentUser.mfaEnrollmentPending));
					if (error.status === 401 && preAuthTokenActive) {
						return of(null);
					}
					if (error.status === 401 && this.isGuestAuthorizationDenial(req, error, currentUser)) {
						return this.refuseGuestActionOrLogOut(error, currentUser);
					}
					this._authenticationService.showAuthModal(error);
					return of(null);
				} else {
					return throwError(error);
				}
			})
		);

	}

	/**
	 * XD-62456. The backend refuses a guest calling an operation outside the guest allowlist with the SAME
	 * 401 / errorCode 90 / "You are not authorized to access this application." it returns for a missing or
	 * expired token (legacy BasicAuthenticationInvoker and net10 GuestAccessFilter alike), so the body alone
	 * cannot tell "not allowed" from "session over". For a signed-in guest the session is checked once: a live
	 * session means the call was refused, not that the session ended. Authentication calls are excluded, so the
	 * check can never trigger itself.
	 */
	private isGuestAuthorizationDenial(req: HttpRequest<any>, error: any, currentUser: any): boolean {
		return !!currentUser?.token
			&& isGuestUser(currentUser)
			&& !/\/v2\/Authentication(\/|$)/i.test(req.url)
			&& String(error?.error?.errorCode) === '90';
	}

	// If IsAlive itself answers 401, the generic branch above has already shown the login and returns of(null),
	// which HttpClient filters out (it only passes HttpResponse events): renewSession() completes WITHOUT
	// emitting, so the check completes empty, switchMap never runs and no second login modal is opened.
	private refuseGuestActionOrLogOut(error: any, currentUser: any): Observable<any> {
		return this.sharedGuestSessionCheck(currentUser).pipe(
			switchMap((outcome) => {
				if (outcome === 'refused') {
					// Session intact: hand the caller an explained refusal instead of tearing the session down.
					// body: the redacted request payload attached above, kept so ErrorService still logs it.
					return throwError(() => Object.assign(new HttpErrorResponse({
						error: {...(error?.error ?? {}), generalMsg: 'Not available', detailedMsg: GUEST_ACTION_NOT_AVAILABLE_MSG},
						headers: error?.headers,
						status: error?.status,
						statusText: error?.statusText,
						url: error?.url ?? undefined
					}), {body: error?.body}));
				}
				if (outcome === 'guestOrderingDisabled') {
					// The backend refuses every guest request once guest ordering is switched off, while IsAlive still
					// answers true: end the session and say why, as a fresh guest login would.
					this._authenticationService.showAuthModal({
						status: error?.status,
						error: {...(error?.error ?? {}), generalMsg: 'Not available', detailedMsg: GUEST_ORDERING_DISABLED_MSG}
					});
					return of(null);
				}
				this._authenticationService.showAuthModal(error);
				return of(null);
			})
		);
	}

	private sharedGuestSessionCheck(currentUser: any): Observable<GuestRefusalOutcome> {
		if (!this.guestSessionCheck$) {
			this.guestSessionCheck$ = this._authenticationService.renewSession(currentUser).pipe(
				switchMap((alive): Observable<GuestRefusalOutcome> => alive === true
					// A failed options read keeps the refusal: it must not log out a guest whose session is fine.
					? this._authenticationService.isGuestOrderingAllowed().pipe(
						map((allowed): GuestRefusalOutcome => allowed ? 'refused' : 'guestOrderingDisabled'),
						catchError(() => of<GuestRefusalOutcome>('refused')))
					: of<GuestRefusalOutcome>('sessionEnded')),
				catchError(() => of<GuestRefusalOutcome>('sessionEnded')),
				finalize(() => { this.guestSessionCheck$ = null; }),
				shareReplay({bufferSize: 1, refCount: false})
			);
		}
		return this.guestSessionCheck$;
	}
}
